import { API_COMMON_URL } from '../constants/constants';
import {
    cartCartSelector,
    cartIncludedProductsSelector,
    cartMainProduct,
} from '../selectors/cartSelector';
import { sessionKey } from '../selectors/pagesSelectors';
import {
    CHECKOUT,
    PAYMENT,
    PROCEED_TO_CHECKOUT_AFTER_AUTH,
    SUBSCRIPTION,
    TOKEN_ERROR_LIST
} from '../constants/checkoutConstants';
import { STRIPE_PUBLIC_KEY } from '../../general/config';
import { loadStripe } from '@stripe/stripe-js';
import isRenewAllowed from '../../resolvers/isRenewAllowed';
import { getLogger } from '../../lib/devMode';
import isPeriodDifferentByProducts from '../../resolvers/isPeriodDifferentByProducts';
import convertToBoolean from '../../lib/string/convertToBoolean';
import { toast } from 'react-toastify';
import {logout} from "./authActions";
import {setAccountFormIsVisible, setAccountFormRegisterState} from "./pagesActions";

export const checkout = (provider = 'stripe') => (dispatch, getState) => {
    // Unset the redirection to payment gateway on auth if it's set.
    dispatch({ type: PROCEED_TO_CHECKOUT_AFTER_AUTH, data: null });

    const cart = cartCartSelector(getState());
    const user_token = sessionKey(getState());
    const mainProduct = cartMainProduct(getState());
    const autoUpgradeFlag = typeof cart?.autoUpgrade === 'string'
        ? convertToBoolean(cart?.autoUpgrade) && mainProduct?.product_id === 2
        : !!cart?.autoUpgrade && mainProduct?.product_id === 2;
    const featuredIncludedProducts = cartIncludedProductsSelector(getState())?.filter(
        (product) => {
            const inclusionFlag = product?.product_id !== mainProduct?.product_id && !!product?.tariff
                && !isPeriodDifferentByProducts(mainProduct, product);
            getLogger().debug({
                debug: 'Checkout > Featured product item',
                product,
                inclusionFlag
            })
            return inclusionFlag;
        }
    ).map(product => product.product_id);

    const postData = {
        user_token: user_token,
        interval: mainProduct?.period?.billing_period,
        interval_count: mainProduct?.period?.interval,
        // Before submitting renew flag - ensure that it's possible to renew.
        mode: isRenewAllowed(mainProduct?.period, mainProduct?.product_id) && cart?.renew ? SUBSCRIPTION : PAYMENT,
        tariff_id: mainProduct?.tariff?.tariff_id,
        extra_package: Number(mainProduct?.includeExtra),
        auto_upgrade: autoUpgradeFlag ? 1 : 0,
        cancel_url: window.location.origin + window.location.pathname + window.location.search,
        source: 'P2' // Stands to the current universal payments page.
    };

    provider = typeof provider === 'string' ? provider.trim().toLowerCase() : null;
    if (!!provider && ['paypal', 'stripe'].includes(provider)) {
        postData.provider = provider;
    } else {
        throw new Error('Payment provider is not available');
    }

    if (featuredIncludedProducts?.length) {
        postData.featured_product_ids = featuredIncludedProducts;
    }

    // SSL product.
    // If there is no domain selected - is considered purchase of a new one.
    // If at least one domain is selected - return "multiservice_ids" set.
    // For details @see: https://doboard.com/1/task/4966#comment_36078
    if (mainProduct?.product_id === 5 && cart?.domains?.length) {
        postData.multiservice_ids = cart?.domains;
    } else if (mainProduct?.product_id === 7 && cart?.account) {
        // For doBoard payment - account ID must be specified.
        postData.multiservice_ids = cart?.account;
    }

    dispatch({
        type: CHECKOUT,
        CallAPI: API_COMMON_URL + '?method_name=bill_checkout',
        postData: postData,
        showTokenError: true
    }).then(async (data) => {
        if (data?.apiData?.error_message) {
            if ( TOKEN_ERROR_LIST.includes(data?.apiData?.error_message)) {
                dispatch(logout()).then(() => {
                    dispatch(setAccountFormIsVisible(true));
                    dispatch(setAccountFormRegisterState(true));
                });
            } else {
                toast.error(`Order payment failed: ${data?.apiData?.error_message} (${data?.apiData?.error_no})`);
            }
        } else if (data?.apiData?.data?.operation_status) {
            if (provider === 'stripe') {
                const sessionId = data?.apiData?.data?.session_id;
                const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
                await stripe.redirectToCheckout({ sessionId });
            } else if (provider === 'paypal') {
                window.location.href = data?.apiData?.data?.href;
            } else {
                toast.error('Not supported payment gateway');
            }
        }
    }).catch((error) => getLogger().error(error));
};
